import * as ko from "knockout";
import { isBrowser } from "targetenv";

import { PliCallSignInHandlerSettings } from "../core/call";
import { PliNavigationForm, PliNavigationParams } from "../forms/forms";
import { PopupForm, PopupFormViewModelClass, registerPopupForm } from "../forms/popupForm";

import { SignInViewModel } from "./signInViewModel";
import { registerAnimatedPageBindingHandler } from "./animatedPage";


/**
 * Defines settings recognized by "SignInForm.open" method.
 */
export interface SignInFormSettings extends PliNavigationParams, PliCallSignInHandlerSettings {
    /**
     * A flag indicating open/close should animate (falsy, default) or happen instantly (true).
     */
    disableAnimation?: boolean;
}


/**
 * Implements built-in sign-in form handler.
 *
 * This form is displayed when server returns "401: Unauthorized" status
 * and allows user to sign-in using the provided credentials.
 */
export class SignInForm extends PopupForm {

    /**
     * Creates new instance of "SignInForm", validating and storing specified "originalForm" reference.
     *
     * Original form is expected to be the default form with ID "signIn",
     * created during HTML page load by looking for elements that have
     * "data-pli-form" attribute.
     */
    constructor(originalForm: PliNavigationForm, viewModelConstructor: PopupFormViewModelClass) {
        super(originalForm, viewModelConstructor);

        if (isBrowser) {
            registerAnimatedPageBindingHandler();
            this._$dialog = this.$form.find("#signInDialog");
        }
    }

    /**
     * Closes this form. Should not be called externally for this form (the form will auto-close when done).
     *
     * Closing of sign-in form can be controlled by specifying
     * "settings.deferClosingUntil" when calling "open".
     */
    close(): this {
        if (this._viewModelActiveSubscription) { throw new Error("\"pli.forms.registry.signIn.close\" should not be called, the form will auto-close."); }
        return <this>super.close();
    }

    /**
     * Gets observable that indicates completion of sign-in process by notifying observers with "true" value.
     *
     * Compatible with return value of "call.PliCallSignInHandler".
     * Will fail if called before the first "open" or after auto-close.
     */
    get isDone(): ko.Observable<boolean> {
        if (this._viewModelDone === null) { throw new Error("\"pli.forms.registry.signIn.isDone\" can only be called after \"open\""); }
        return this._viewModelDone;
    }

    /**
     * Opens this form. Typically called by "pli/call" when "401: Unauthorized" response is received.
     */
    open(settings?: SignInFormSettings): this {
        return <this>super.open(settings);
    }



    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Overrides "PopupForm"'s closing logic by starting a VelocityJS closing
     * animation and only cleaning up once the animation has completed.
     */
    protected _close(viewModel: SignInViewModel, originalForm: PliNavigationForm): void {
        if (isBrowser) {
            this._$dialog.velocity("reverse");
            this.$form.velocity("reverse", {
                complete: (): void => {
                    this._animate = null;
                    this.$form.removeClass("active");
                    viewModel.reset();
                }
            });
        } else {
            viewModel.reset();
        }
    }

    /**
     * Auto-closes this form when viewmodel's "active" computed property changes to false.
     */
    private _closeWhenViewModelDeactivated(viewModelActive: boolean): void {
        if (!viewModelActive) {
            this._viewModelActiveSubscription.dispose();
            this._viewModelActiveSubscription = null;
            this._viewModelDone = null;
            this.close();
        }
    }

    /**
     * Overrides "PopupForm"'s opening logic by starting a VelocityJS opening animation.
     */
    protected _open(settings: SignInFormSettings, viewModel: SignInViewModel, originalForm: PliNavigationForm): void {
        const activationInfo = viewModel.activate(settings);
        this._viewModelDone = activationInfo.done;
        if (activationInfo.active) {
            this._viewModelActiveSubscription = activationInfo.active.subscribe(this._closeWhenViewModelDeactivated, this);
            if (isBrowser) {
                this._animate = !(settings && settings.disableAnimation);
                this.$form.css("opacity", 0).addClass("active").velocity({
                    properties: { opacity: [1, 0] },
                    options: {
                        duration: this._animate ? 750 : 0,
                        easing: "easeOutSine",
                        complete: (): JQuery => this.$form.removeAttr("style")
                    }
                });
                this._$dialog.css("opacity", 0).velocity({
                    properties: { translateY: ["0px", "100px"], opacity: [1, 0, "linear"] },
                    options: {
                        duration: this._animate ? 500 : 0,
                        easing: "easeOutSine",
                        delay: 250,
                        complete: (): JQuery => this._$dialog.removeAttr("style")
                    }
                });
            }
        }
    }

    /** Flag indicating whether open/close methods will use animations */
    private _animate: boolean = null; // set in "open", reset in "close" (to null)

    /** Reference to sign-in dialog's root DOM element (child of "_originalForm.$form") */
    private _$dialog: JQuery; // readonly, initialized in constructor

    /** Subscription for current "active" value observer. */
    private _viewModelActiveSubscription: ko.subscription<boolean> = null; // set in "open", disposed and reset in "_closeWhenViewModelDeactivated"

    /** Current "done" observable. Returned from "isDone". */
    private _viewModelDone: ko.Observable<boolean> = null; // set in "open", reset in "_closeWhenViewModelDeactivated"

}


/**
 * Opens sign-in form with the given settings.
 *
 * To use, render "signIn" form to the webpage by calling
 * extension method "Pandell.Firestorm.WebLibrary.Pli.Razor.CreateSignInForm"
 * in Razor (e.g. on "start.cshtml"). The signin form will then
 * be used automatically when "401: Unauthorized" PLI call
 * response is encountered.
 */
export function openSignInForm(settings?: SignInFormSettings): SignInForm {
    const signInForm = <SignInForm>registerPopupForm("signIn", SignInViewModel, SignInForm);
    return signInForm.open(settings);
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms-signin/signInForm.ts